import { Box, Button, Stack, Typography } from '@mui/material';
import AuthenticatedImage from '../../core/AuthenticatedImage';
import { BlogPostPreview } from '../blogSchemas';
import { formatDateTime } from '../blogUtils';
import TagChip from '../../core/TagChip';

const WAPPEN_SRC = `${process.env.PUBLIC_URL}/Wappen_PVTA.png`;

interface BlogFeaturedCardProps {
    post: BlogPostPreview;
    onClick: (id: number) => void;
}

const BlogFeaturedCard: React.FC<BlogFeaturedCardProps> = ({ post, onClick }) => {
    const mainImageUrl = post.coverImageUrl ?? post.previewImages[0]?.url ?? null;

    return (
        <Box
            role="button"
            tabIndex={0}
            onClick={() => onClick(post.id)}
            onKeyDown={(keyboardEvent) => {
                if (keyboardEvent.key === 'Enter' || keyboardEvent.key === ' ') {
                    keyboardEvent.preventDefault();
                    onClick(post.id);
                }
            }}
            sx={{
                position: 'relative',
                overflow: 'hidden',
                borderRadius: 'var(--pvta-radius-lg)',
                boxShadow: 'var(--pvta-shadow-card)',
                minHeight: { xs: 260, md: 340 },
                display: 'flex',
                color: '#ffffff',
                cursor: 'pointer',
                backgroundImage: 'linear-gradient(135deg, #1f4d2b 0%, #0f2f1a 100%)',
                transition: 'transform 160ms ease, box-shadow 160ms ease',
                '&:hover': { transform: 'translateY(-2px)', boxShadow: 'var(--pvta-shadow-hover)' },
            }}
        >
            {mainImageUrl ? (
                <AuthenticatedImage
                    url={mainImageUrl}
                    alt={post.title}
                    sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
                />
            ) : (
                <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Box component="img" src={WAPPEN_SRC} alt="" sx={{ height: 96, opacity: 0.85 }} />
                </Box>
            )}
            <Box
                sx={{
                    position: 'absolute',
                    inset: 0,
                    background: 'linear-gradient(180deg, rgba(15,47,26,0.05) 0%, rgba(15,47,26,0.55) 55%, rgba(15,47,26,0.9) 100%)',
                }}
            />
            <Box sx={{ position: 'relative', mt: 'auto', p: { xs: 2.5, md: 4 }, width: '100%' }}>
                {post.tags.length > 0 && (
                    <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" sx={{ mb: 1.5 }}>
                        {post.tags.slice(0, 4).map((tag) => (
                            <TagChip key={tag.id} tag={tag} />
                        ))}
                    </Stack>
                )}
                <Typography
                    sx={{
                        fontFamily: 'Georgia, "Times New Roman", serif',
                        fontWeight: 700,
                        fontSize: { xs: 24, md: 34 },
                        lineHeight: 1.12,
                        textShadow: '0 2px 12px rgba(0,0,0,0.35)',
                    }}
                >
                    {post.title}
                </Typography>
                {(post.teaser || post.fallbackText) && (
                    <Typography
                        sx={{
                            mt: 1,
                            opacity: 0.92,
                            maxWidth: 680,
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                        }}
                    >
                        {post.teaser || post.fallbackText}
                    </Typography>
                )}
                <Typography variant="caption" sx={{ display: 'block', mt: 1.5, opacity: 0.85 }}>
                    {post.author.name} · {formatDateTime(post.createdAt)}
                </Typography>
                <Button
                    onClick={(clickEvent) => {
                        clickEvent.stopPropagation();
                        onClick(post.id);
                    }}
                    sx={{
                        mt: 2.5,
                        backgroundColor: '#ffffff',
                        color: '#0f2f1a',
                        borderRadius: 999,
                        fontWeight: 700,
                        px: 2.5,
                        '&:hover': { backgroundColor: '#f1ede3' },
                    }}
                >
                    Beitrag lesen
                </Button>
            </Box>
        </Box>
    );
};

export default BlogFeaturedCard;
